import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as TicketIcon } from '../assets/icons/ticket-entries.svg';
import apiService from '../services/api';
import { boostBannerTitle, networkBannerBoost } from '../utils/dreamBoost';
import { formatCompactDuration } from '../utils/dreamSummary';
import '../styles/DreamBoostBanner.css';

// Same texts and behaviour as the embed banner (dreamBoost.js).
function drawPhrase(summary) {
  const name = summary?.period?.name ? String(summary.period.name).trim() : '';
  return name ? `the ${name} draw` : 'the draw';
}

function dreamRewardImage(summary) {
  const listed = Array.isArray(summary?.featured_draws)
    ? summary.featured_draws.filter(Boolean)[0]
    : null;
  const draw = listed || summary?.featured_draw || (Array.isArray(summary?.prizes) ? summary.prizes[0] : null);
  if (!draw) return '';
  return draw.cover_image_url || (Array.isArray(draw.image_urls) ? draw.image_urls[0] : '') || draw.cover_image || '';
}

function ClockIcon() {
  return (
    <svg className="dream-boost-clock" viewBox="0 0 12 13" fill="none" aria-hidden="true">
      <circle cx="5.55" cy="7.2" r="4.4" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M3.9 0.8h3.3M5.55 0.8v1.1M5.55 5v2.2h2.2"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function GameDayBoost({ summary, boost, now, onShop }) {
  const image = dreamRewardImage(summary);
  const left = formatCompactDuration(Math.max(0, boost.endsAt - now));
  return (
    <div className="dream-game-day">
      <div className="dream-game-day-hero">
        {image ? <img className="dream-game-day-photo" src={image} alt="" /> : null}
        <div className="dream-game-day-top">
          <span className="dream-game-day-mark">
            <TicketIcon className="dream-boost-icon" aria-hidden="true" />
            {`${boost.multiplier}X`}
          </span>
          <span className="dream-game-day-ends">
            <ClockIcon />
            <span className="dream-boost-time">{`Ends in ${left}`}</span>
          </span>
        </div>
      </div>
      <div className="dream-game-day-body">
        <div className="dream-game-day-title">{boostBannerTitle(boost)}</div>
        <p className="dream-game-day-note">{`Every purchase earns ${boost.multiplier}X entries into ${drawPhrase(summary)}.`}</p>
        <button type="button" className="dream-game-day-cta" onClick={onShop}>
          Find places to earn
        </button>
      </div>
    </div>
  );
}

// Reads the same Dream summary the embed reads. Nothing renders until it arrives,
// when the school's program is off, or when no school-wide boost is running.
function DreamBoostBanner() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const boost = networkBannerBoost(summary, now);
  const endsAt = boost ? boost.endsAt : 0;

  useEffect(() => {
    let cancelled = false;
    apiService
      .getDreamSummary()
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!endsAt) return undefined;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [endsAt]);

  if (!boost) return null;
  const openShop = () => navigate('/shop');
  if (boost.showImage) {
    return <GameDayBoost summary={summary} boost={boost} now={now} onShop={openShop} />;
  }
  return (
    <div className="dream-boost">
      <div className="dream-boost-top">
        <span className="dream-boost-mark">
          <TicketIcon className="dream-boost-icon" aria-hidden="true" />
          {`${boost.multiplier}X`}
        </span>
        <span className="dream-boost-ends">
          <ClockIcon />
          <span className="dream-boost-time">{`Ends in ${formatCompactDuration(Math.max(0, boost.endsAt - now))}`}</span>
        </span>
      </div>
      <div className="dream-boost-title">{boostBannerTitle(boost)}</div>
      <p className="dream-boost-note">{`Every purchase earns ${boost.multiplier}X entries into ${drawPhrase(summary)}.`}</p>
      <button type="button" className="dream-boost-cta" onClick={openShop}>
        Find places to earn
      </button>
    </div>
  );
}

export default DreamBoostBanner;
